/**
 * Pièces en attente — `WS2_EA_TAB_PMQ` (fiche 22780, contract §6
 * `boby-ws2-ea-tab-pmq`, no caller parameter: `SES` is appended by the
 * proxy and `perPage` defaults to `9999` server-side).
 *
 * Maps each VEOS row to a {@link PendingPiece} view model consumed by
 * `extranet.pendingPiecesListCard`. Null-safe: a `null` / blank VEOS value
 * becomes `null` (« ne rien afficher »), never the text "null".
 */

import { fetchErpRows } from './erpProxy';
import { formatMsDate, formatText, type VeosScalar } from '../utils/format';

/** Endpoint slug of `WS2_EA_TAB_PMQ` (contract §6). */
export const TAB_PMQ_ENDPOINT = 'boby-ws2-ea-tab-pmq';

/** One raw `WS2_EA_TAB_PMQ` row (fiche 22780). */
export interface PendingPieceRow {
  /** `POLICE` | `SINISTRE` — what the piece is attached to. */
  ORIGINE: string | null;
  ID_POL: number | string | null;
  ID_SIN: number | string | null;
  NUM_POL: string | null;
  ID_NPOL: number | string | null;
  NOMCOMPLETASS: string | null;
  CD_PIE: string | null;
  LIB_PIE: string | null;
  /** Request date — Unix timestamp in ms. */
  DATDEMANDE_PMD: number | string | null;
  ID_PMD: number | string | null;
  /** Raw status, e.g. `EN ATTENTE`. */
  STATUT: string | null;
  /** Display reference, e.g. « Contrat 1237452 ». */
  REFERENCE: string | null;
}

/** What a pending piece is attached to (`ORIGINE`). */
export type PendingPieceOrigin = 'POLICE' | 'SINISTRE';

/** A pending piece, as rendered by `extranet.pendingPiecesListCard`. */
export interface PendingPiece {
  /** Stable row id (`ID_PMD`; the row index when VEOS returned none). */
  id: string;
  /** Piece label (`LIB_PIE`). */
  libelle: string | null;
  /** Request date `JJ/MM/AAAA` (`DATDEMANDE_PMD`, read in Europe/Paris). */
  dateDemande: string | null;
  /** Raw status (`STATUT`). */
  statut: string | null;
  /** `ORIGINE` — `null` when missing or unknown. */
  origine: PendingPieceOrigin | null;
  /** Display reference of the dossier (`REFERENCE`). */
  reference: string | null;
  /** Contract id (`ID_NPOL`) — link target of a `POLICE` piece. */
  idNpol: string | null;
  /** Claim id (`ID_SIN`) — link target of a `SINISTRE` piece. */
  idSin: string | null;
  /** Contract number (`NUM_POL`) — technical, not displayed. */
  numPol: string | null;
  /** Insured full name (`NOMCOMPLETASS`) — technical, not displayed. */
  insured: string | null;
}

/** Trimmed text, `null` when missing / blank. */
function textOrNull(value: VeosScalar): string | null {
  const text = formatText(value);
  return text === '' ? null : text;
}

/** Map `ORIGINE` to a known origin (case / whitespace tolerant). */
export function pendingPieceOriginOf(value: VeosScalar): PendingPieceOrigin | null {
  const origin = formatText(value).toUpperCase();
  return origin === 'POLICE' || origin === 'SINISTRE' ? origin : null;
}

/** Map one `WS2_EA_TAB_PMQ` row to its view model. */
export function mapPendingPiece(row: PendingPieceRow, index: number): PendingPiece {
  const dateDemande = formatMsDate(row.DATDEMANDE_PMD);
  return {
    id: textOrNull(row.ID_PMD) ?? `pmd-${String(index)}`,
    libelle: textOrNull(row.LIB_PIE),
    dateDemande: dateDemande === '' ? null : dateDemande,
    statut: textOrNull(row.STATUT),
    origine: pendingPieceOriginOf(row.ORIGINE),
    reference: textOrNull(row.REFERENCE),
    idNpol: textOrNull(row.ID_NPOL),
    idSin: textOrNull(row.ID_SIN),
    numPol: textOrNull(row.NUM_POL),
    insured: textOrNull(row.NOMCOMPLETASS),
  };
}

/**
 * Fetch the session's pending pieces (API order preserved).
 *
 * @throws Error with the ERP message on any failure (see `erpProxy`).
 */
export async function fetchPendingPieces(
  registrySlug: string | null | undefined,
): Promise<PendingPiece[]> {
  const rows = await fetchErpRows<PendingPieceRow>(registrySlug, TAB_PMQ_ENDPOINT);
  return rows.map(mapPendingPiece);
}

/** React Query key of the list — scoped by environment and session. */
export function pendingPiecesQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
): readonly unknown[] {
  return ['extranet', 'pending-pieces', registrySlug, sessionId];
}
